import {
  EmailRecommendedVariant,
  GmailAnnotatedEmailRecommendedVariant,
  InteractiveCartProduct,
  InteractiveProductVariant,
} from "../email-product-recommendation";
import { Provider } from "../order";

export const MOCK_PRODUCT_IMAGE_URL = `https://placehold.co/120x120?text=${encodeURIComponent("Personalized Content")}`;
export const MOCK_CUSTOM_FIELD = "Custom field";

export function generateEmailRecommendedVariants(
  amount: number,
): GmailAnnotatedEmailRecommendedVariant[] {
  return Array.from({ length: amount }, (_, i) => ({
    productId: `${i}`,
    variantId: `${i * 2}`,
    priceSet: {
      presentment_money: { amount: Number(amount), currency_code: "USD" },
    },
    title: "{Product Name}",
    variantTitle: "Variant title",
    image: { src: MOCK_PRODUCT_IMAGE_URL },
    pdpUrl: "https://example.com",
    price: 12.34,
    quantity: 1,
    alreadyInCart: false,
    provider: Provider.SHOPIFY,
    croppedImageUrl: null,
  }));
}

export function generateEmailRecommendedVariantsForE2E(
  amount: number,
): GmailAnnotatedEmailRecommendedVariant[] {
  return generateEmailRecommendedVariants(amount).map((variant, i) => ({
    ...variant,
    title: `Mock Product ${i + 1}`,
    pdpUrl: `https://example.com/product-${i + 1}`,
    image: { src: `https://placehold.co/120x120?text=Product+${i + 1}` },
  }));
}

export function mockInteractiveCartProductsForE2E(
  amount: number,
): InteractiveCartProduct[] {
  const staticRecommendedVariants =
    generateEmailRecommendedVariantsForE2E(amount);
  return mockStaticToInteractiveRecommendedProducts(staticRecommendedVariants);
}

const mockVariantDimensions = [
  { name: "Size", options: ["Small", "Medium", "Large"] },
  { name: "Color", options: ["Red", "Green", "Blue"] },
  { name: "Material", options: ["Cotton", "Polyester", "Wool"] },
];

// FIXME since the AMP version requires real product IDs to get recommendations,
// it looks blank in Storybook. Fixing this is a bit involved, would require either the backend
// to support mocks, or for Storybook to use real product IDs.
function mockStaticToInteractiveRecommendedProducts(
  staticRecommendedVariants: EmailRecommendedVariant[],
): InteractiveCartProduct[] {
  return staticRecommendedVariants.map((product, productIndex) => {
    // Determine a deterministic number of dimensions for this product.
    const numDimensions = (productIndex % mockVariantDimensions.length) + 1;
    const usedVariantDimensions = mockVariantDimensions.slice(0, numDimensions);

    // Decide how many variants to create. Approx 25% of options should be invalid.
    // Here we choose the maximum number of options among the used dimensions.
    // The rest will be considered invalid options.
    const variantCount = Math.ceil(
      usedVariantDimensions.reduce((acc, dim) => acc * dim.options.length, 1) *
        0.75,
    );

    const variants: InteractiveProductVariant[] = [];
    for (let variantIndex = 0; variantIndex < variantCount; variantIndex++) {
      // Each variant gets one option per dimension using modulus arithmetic.
      const selectedOptions = usedVariantDimensions.map(
        (dimension, dimIndex) => {
          const optionIndex =
            (variantIndex + dimIndex) % dimension.options.length;
          return {
            name: dimension.name,
            value: dimension.options[optionIndex],
          };
        },
      );

      const variantTitle = `${product.title} - ${selectedOptions
        .map((opt) => `${opt.name}: ${opt.value}`)
        .join(", ")}`;

      variants.push({
        id: `${productIndex}-${variantIndex}` as `${number}`,
        title: variantTitle,
        formattedPrice: "${XXX.XX}",
        price: { amount: variantIndex + 1.99, currencyCode: "USD" },
        image: MOCK_PRODUCT_IMAGE_URL,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        selectedOptions,
      });
    }

    return {
      id: `${product.productId}` as `${number}`,
      title: product.title,
      variantDimensions: usedVariantDimensions,
      variants,
      primaryImageUrl: MOCK_PRODUCT_IMAGE_URL,
    };
  });
}

export function mockInteractiveCartProducts(
  amount: number,
): InteractiveCartProduct[] {
  const staticRecommendedVariants = generateEmailRecommendedVariants(amount);
  return mockStaticToInteractiveRecommendedProducts(staticRecommendedVariants);
}

export function generateMockInteractiveCartResponse(
  cartId: string,
  storeUrl: string,
  productCount = 3,
) {
  const mockProducts = mockInteractiveCartProductsForE2E(productCount);
  return {
    id: cartId,
    sanitizedId: cartId.split("/").pop()?.split("?")?.[0] ?? "",
    storeUrl,
    cartQuantity: 0,
    lineItems: mockProducts.map((product, index) => ({
      sanitizedId: product.id,
      variant: {
        sanitizedId: product.variants[0]?.id || product.id,
        title: product.variants[0]?.title || product.title,
        product: { sanitizedId: product.id, title: product.title },
      },
      quantity: 0,
      quantityExactlyOne: false,
      isRecommendation: true,
      index,
      idNumber: product.id,
      imageUrl: product.primaryImageUrl,
      formattedCost: product.variants[0]?.formattedPrice || "$0.00",
      isAvailable: true,
      productUrl: `${storeUrl}/products/mock-product-${index}`,
      selectedOptions: product.variants[0]?.selectedOptions || [],
      isRedo: false,
      variantDimensions: product.variantDimensions.map((dim) => ({
        name: dim.name,
        selectedOption: dim.options[0] || "",
        options: dim.options.map((opt) => ({
          label: opt,
          value: opt,
          selected: false,
        })),
      })),
    })),
    formattedCost: "$0.00",
    checkoutUrl: `${storeUrl}/cart`,
    cost: {
      checkoutChargeAmount: { amount: "0.00", currencyCode: "USD" as const },
      subtotalAmount: { amount: "0.00", currencyCode: "USD" as const },
      totalAmount: { amount: "0.00", currencyCode: "USD" as const },
      totalDutyAmount: null,
      totalTaxAmount: null,
    },
  };
}
